import { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  useQuery,
  useAction,
  useMutation,
  usePaginatedQuery,
  optimisticallyUpdateValueInPaginatedQuery,
} from "convex/react";
import { api } from "../../convex/_generated/api";
import { Button } from "@/components/ui/Button";
import { ProgressRing } from "@/components/ui/ProgressRing";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { MessageBubble } from "@/components/MessageBubble";
import { TypingIndicator } from "@/components/TypingIndicator";
import { cn } from "@/lib/cn";
import { formatKcalBalance } from "../lib/kcal";
import { clientLogTimes, localDayStartMs } from "../lib/dayHelpers";
import { useLoggingFlow, type LoggingFlowReviewItem } from "../lib/logging-flow";
import { createCheckinResponder } from "./plan-utils";
import { shouldApplyThreadResult } from "./chat-thread-request";
import { AudioLines, ImagePlus, Loader2, MessageCircle, Send, Square, X } from "lucide-react";
import type { MessageDoc } from "@convex-dev/agent/validators";
import type { Id } from "../../convex/_generated/dataModel";
import { isRecord } from "@/lib/records";
import { canSendMessage } from "@/screens/chat-guards";
import {
  parseChatMessages,
  type FailedChatMessage,
} from "@/screens/chat-message-parser";
import { APP_ERROR_KINDS } from "../lib/app-errors";
import {
  canStartApprovalDecision,
  getApprovalContinuationPlan,
  mergeApprovalRecoveries,
  type ApprovalRecovery,
} from "@/screens/approval-recovery";
import { normalizeChatMessages } from "@/screens/chat-message-order";
import { isNewChatCommand } from "@/screens/chat-commands";
import { ChatThreadMenu } from "@/components/ChatThreadMenu";
import { CreditDrawer, isCreditError } from "../components/CreditDrawer";
import {
  appendTranscriptAtSelection,
  shouldRestoreTextareaSelection,
} from "./textarea-selection";
import {
  encodeBlobAsBase64,
  isVoiceRecordingSupported,
  startVoiceRecording,
  transcribeAudioRef,
  VOICE_RECORDING_ERROR_MESSAGES,
} from "../lib/voice-recorder";
import type { ActiveVoiceRecording } from "../lib/voice-recorder";
import {
  beginImageUpload,
  failImageUpload,
  imageFileValidationError,
  uploadImagePipeline,
  type ImageUploadState,
} from "../lib/image-upload";

export function insertVoiceTranscript(
  transcript: string,
  appendTranscript: (text: string) => void,
): boolean {
  if (transcript.trim().length === 0) return false;
  appendTranscript(transcript);
  return true;
}

export function buildChatSendArgs<ImageStorageId extends string>(args: {
  readonly threadId: string;
  readonly prompt: string;
  readonly imageStorageId?: ImageStorageId;
  readonly clientNow: number;
}) {
  return {
    threadId: args.threadId,
    prompt: args.prompt,
    ...(args.imageStorageId === undefined ? {} : { imageStorageId: args.imageStorageId }),
    clientNow: args.clientNow,
    ...clientLogTimes(args.clientNow),
  };
}

export function collectNewCreditFailureMessageIds(
  failures: readonly FailedChatMessage[],
  handledMessageIds: Set<string>,
): readonly string[] {
  const creditFailureMessageIds: string[] = [];
  failures.forEach((failure) => {
    if (handledMessageIds.has(failure.messageId)) return;
    handledMessageIds.add(failure.messageId);
    if (failure.errorKind === APP_ERROR_KINDS.insufficientCredits) {
      creditFailureMessageIds.push(failure.messageId);
    }
  });
  return creditFailureMessageIds;
}

export interface ChatProps {
  onSwitchScreen?: (screen: string) => void;
}

function hasApprovalRequest(message: unknown, approvalId: string): boolean {
  if (!isRecord(message)) return false;
  const messageField = message.message;
  if (!isRecord(messageField)) return false;
  const content = messageField.content;
  if (!Array.isArray(content)) return false;
  return content.some(
    (part) =>
      isRecord(part) &&
      part.type === "tool-approval-request" &&
      part.approvalId === approvalId,
  );
}

function markApprovalDecided(
  message: unknown,
  approvalId: string,
  approved: boolean,
  reason?: string,
): unknown {
  if (!isRecord(message)) return message;
  const messageField = message.message;
  if (!isRecord(messageField)) return message;
  const content = messageField.content;
  if (!Array.isArray(content)) return message;
  const newContent = content.map((part) => {
    if (
      isRecord(part) &&
      part.type === "tool-approval-request" &&
      part.approvalId === approvalId
    ) {
      return { ...part, type: "tool-approval-response", approved, reason };
    }
    return part;
  });
  return { ...message, message: { ...messageField, content: newContent } };
}

export default function Chat({ onSwitchScreen }: ChatProps) {
  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  const [input, setInput] = useState("");
  const [rows, setRows] = useState(1);
  const [isSending, setIsSending] = useState(false);
  const [approvalRecoveries, setApprovalRecoveries] = useState<Record<string, ApprovalRecovery>>({});
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [imageUploadState, setImageUploadState] = useState<ImageUploadState>({ phase: "idle" });
  const [attachedImage, setAttachedImage] = useState<{
    readonly storageId: Id<"_storage">;
    readonly previewUrl: string;
    readonly fileName: string;
  } | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isStartingNewChat, setIsStartingNewChat] = useState(false);
  const [showThreadHistory, setShowThreadHistory] = useState(false);
  const [showCreditDrawer, setShowCreditDrawer] = useState(false);
  const [composerTarget, setComposerTarget] = useState<HTMLElement | null>(null);
  const { startFlow } = useLoggingFlow();
  const bottomRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const approvalBusyRef = useRef(new Set<string>());
  const continuationBusyRef = useRef(new Set<string>());
  const approvalRecoveriesRef = useRef<Record<string, ApprovalRecovery>>({});
  const threadRequestIdRef = useRef(0);
  const preserveScrollAnchorRef = useRef(false);
  const hasInitialScrollEffectRef = useRef(false);
  const previousScrollHeightRef = useRef(0);
  const activeRecordingRef = useRef<ActiveVoiceRecording | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const imagePreviewUrlRef = useRef<string | null>(null);
  const textareaRevisionRef = useRef(0);
  const handledFailedMessageIdsRef = useRef(new Set<string>());

  const startThread = useAction(api.agent.startThread);
  const startNewThread = useAction(api.agent.createAppThread);
  const selectThread = useMutation(api.agent.selectAppThread);
  const sendMessage = useAction(api.agent.sendMessage);
  const respondToWeeklyCheckin = useMutation(api.engine.respondToWeeklyCheckin);
  const transcribeAudio = useAction(transcribeAudioRef);
  const imageLimits = useQuery(api.images.getUploadLimits, {});
  const generateUploadUrl = useMutation(api.images.generateUploadUrl);
  const claimUpload = useMutation(api.images.claimUpload);
  const stableNow = useMemo(() => Date.now(), []);
  const stableClientDayStart = useMemo(() => localDayStartMs(stableNow), [stableNow]);
  const today = useQuery(api.dashboard.getToday, { now: stableNow, clientDayStart: stableClientDayStart });
  const { results: messages, status, loadMore } = usePaginatedQuery(
    api.agent.listThreadMessages,
    threadId ? { threadId } : "skip",
    { initialNumItems: 50 },
  );

  const approve = useMutation(api.agent.approveToolCall).withOptimisticUpdate(
    (localStore, args) => {
      optimisticallyUpdateValueInPaginatedQuery(
        localStore,
        api.agent.listThreadMessages,
        { threadId: args.threadId },
        (message: MessageDoc) => {
          if (hasApprovalRequest(message, args.approvalId)) {
            return markApprovalDecided(
              message,
              args.approvalId,
              args.approved,
              args.reason,
            ) as MessageDoc;
          }
          return message;
        },
      );
    },
  );

  useEffect(() => {
    let cancelled = false;
    const requestId = ++threadRequestIdRef.current;
    startThread({})
      .then((result) => {
        if (shouldApplyThreadResult(requestId, threadRequestIdRef.current, cancelled)) {
          setThreadId(result.threadId);
          console.log("[Frontend][Chat] Thread started", {
            threadId: result.threadId,
          });
        }
      })
      .catch((err: unknown) => {
        console.error("[Frontend][Chat] Failed to start thread", err);
      });
    return () => {
      cancelled = true;
    };
  }, [startThread]);

  const orderedMessages = useMemo(
    () => normalizeChatMessages(messages ?? []),
    [messages],
  );
  const parsedMessages = useMemo(() => {
    return parseChatMessages(orderedMessages);
  }, [orderedMessages]);
  const items = parsedMessages.items;

  const isAgentRunning = useMemo(() => {
    if (isSending) return true;
    const lastMessage = orderedMessages[orderedMessages.length - 1];
    if (isRecord(lastMessage) && lastMessage.status === "pending") return true;
    return false;
  }, [isSending, orderedMessages]);

  const isTyping = useMemo(() => {
    const lastMessage = orderedMessages[orderedMessages.length - 1];
    return isRecord(lastMessage) && lastMessage.status === "pending";
  }, [orderedMessages]);

  const clearThreadState = () => {
    approvalBusyRef.current.clear();
    continuationBusyRef.current.clear();
    approvalRecoveriesRef.current = {};
    setApprovalRecoveries({});
    setInput("");
    setRows(1);
    if (imagePreviewUrlRef.current !== null) URL.revokeObjectURL(imagePreviewUrlRef.current);
    imagePreviewUrlRef.current = null;
    setAttachedImage(null);
    setImageUploadState({ phase: "idle" });
    setImageError(null);
  };

  useEffect(() => {
    return () => {
      activeRecordingRef.current?.cancel();
      activeRecordingRef.current = null;
      if (imagePreviewUrlRef.current !== null) URL.revokeObjectURL(imagePreviewUrlRef.current);
      imagePreviewUrlRef.current = null;
    };
  }, []);

  const handleNewChat = async () => {
    if (isStartingNewChat) return;
    threadRequestIdRef.current += 1;
    setIsStartingNewChat(true);
    console.log("[Frontend][Chat] Starting new chat", { fromCommand: false });
    try {
      const result = await startNewThread({});
      setThreadId(result.threadId);
      clearThreadState();
      setShowThreadHistory(false);
    } catch (err: unknown) {
      console.error("[Frontend][Chat] New chat failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setIsStartingNewChat(false);
    }
  };

  const handleSelectThread = (nextThreadId: string) => {
    if (nextThreadId === threadId) {
      setShowThreadHistory(false);
      return;
    }
    console.log("[Frontend][Chat] Thread selected", { hasThread: nextThreadId.length > 0 });
    threadRequestIdRef.current += 1;
    clearThreadState();
    selectThread({ threadId: nextThreadId })
      .then(() => {
        setThreadId(nextThreadId);
        setShowThreadHistory(false);
      })
      .catch((err: unknown) => {
        console.error("[Frontend][Chat] Thread selection failed", {
          error: err instanceof Error ? err.message : String(err),
        });
      });
  };

  const visibleItems = useMemo(() => {
    return mergeApprovalRecoveries(
      items,
      approvalRecoveries,
      parsedMessages.completedApprovalIds,
    );
  }, [approvalRecoveries, items, parsedMessages.completedApprovalIds]);
  const hasOpenApprovalFlow = visibleItems.some(
    (item) => item.kind === "approval-request",
  );

  useEffect(() => {
    if (parsedMessages.completedApprovalIds.length === 0) return;
    const completedIds = new Set(parsedMessages.completedApprovalIds);
    setApprovalRecoveries((current) => {
      const remaining = Object.fromEntries(
        Object.entries(current).filter(([approvalId]) => !completedIds.has(approvalId)),
      );
      approvalRecoveriesRef.current = remaining;
      return remaining;
    });
    parsedMessages.completedApprovalIds.forEach((approvalId) => {
      approvalBusyRef.current.delete(approvalId);
    });
  }, [parsedMessages.completedApprovalIds]);

  useEffect(() => {
    const creditFailureMessageIds = collectNewCreditFailureMessageIds(
      parsedMessages.failedMessages,
      handledFailedMessageIdsRef.current,
    );
    creditFailureMessageIds.forEach((messageId) => {
      console.warn("[Frontend][Chat] Persisted credit failure detected", {
        messageId,
      });
    });
    if (creditFailureMessageIds.length > 0) setShowCreditDrawer(true);
  }, [parsedMessages.failedMessages]);

  useEffect(() => {
    setComposerTarget(document.querySelector<HTMLElement>('[data-testid="chat-composer-portal"]'));
  }, []);

  useEffect(() => {
    const messageList = messageListRef.current;
    const scrollOwner = messageList?.closest("main");
    if (!scrollOwner || !bottomRef.current) return;
    if (!hasInitialScrollEffectRef.current) {
      hasInitialScrollEffectRef.current = true;
      return;
    }
    if (preserveScrollAnchorRef.current) {
      scrollOwner.scrollTop += scrollOwner.scrollHeight - previousScrollHeightRef.current;
      preserveScrollAnchorRef.current = false;
      return;
    }
    scrollOwner.scrollTo({
      top: scrollOwner.scrollHeight,
      behavior: "smooth",
    });
  }, [visibleItems, isTyping, status]);

  useEffect(() => {
    const scrollOwner = messageListRef.current?.closest("main");
    if (!scrollOwner) return;
    const handleScroll = () => {
      if (scrollOwner.scrollTop > 24 || status !== "CanLoadMore") return;
      preserveScrollAnchorRef.current = true;
      previousScrollHeightRef.current = scrollOwner.scrollHeight;
      console.debug("[Frontend][Chat] Loading older messages", {
        threadSelected: threadId !== undefined,
      });
      loadMore(50);
    };
    scrollOwner.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollOwner.removeEventListener("scroll", handleScroll);
  }, [loadMore, status, threadId]);

  const handleSend = (promptToSend: string) => {
    if (isNewChatCommand(promptToSend)) {
      void handleNewChat();
      return;
    }
    const activeThreadId = threadId;
    if (
      !canSendMessage({
        threadId: activeThreadId,
        prompt: promptToSend,
        hasImage: attachedImage !== null,
        isSending,
        isAgentRunning,
        isUploading: imageUploadState.phase === "uploading",
        approvalBusy:
          hasOpenApprovalFlow ||
          approvalBusyRef.current.size > 0 ||
          continuationBusyRef.current.size > 0,
      })
    ) {
      console.debug("[Frontend][Chat] Send blocked by guard", {
        hasThread: threadId !== undefined,
        promptLength: promptToSend.trim().length,
        hasImage: attachedImage !== null,
        isUploading: imageUploadState.phase === "uploading",
        isSending,
        isAgentRunning,
        approvalBusy:
          hasOpenApprovalFlow ||
          approvalBusyRef.current.size > 0 ||
          continuationBusyRef.current.size > 0,
      });
      return;
    }
    if (activeThreadId === undefined) return;
    console.log("[Frontend][Chat] Message sent", {
      promptLength: promptToSend.length,
      hasImage: attachedImage !== null,
    });
    setIsSending(true);
    setImageError(null);
    const clientNow = Date.now();
    const imageStorageId = attachedImage?.storageId;
    sendMessage(buildChatSendArgs({
      threadId: activeThreadId,
      prompt: promptToSend,
      ...(imageStorageId === undefined ? {} : { imageStorageId }),
      clientNow,
    }))
      .then(() => {
        setInput("");
        setRows(1);
        if (imagePreviewUrlRef.current !== null) URL.revokeObjectURL(imagePreviewUrlRef.current);
        imagePreviewUrlRef.current = null;
        setAttachedImage(null);
        setIsSending(false);
        console.log("[Frontend][Chat] Message accepted", { hadImage: imageStorageId !== undefined });
      })
      .catch((err: unknown) => {
        setIsSending(false);
        if (isCreditError(err)) {
          setShowCreditDrawer(true);
          console.warn("[Frontend][Chat] Credit or quota issue detected", { phase: "send" });
        }
        console.error("[Frontend][Chat] Send failed", {
          phase: "send",
          error: err instanceof Error ? err.message : String(err),
        });
        if (imageStorageId !== undefined) {
          setImageError("Message failed to send. Your image is still attached — try again.");
        }
      });
  };

  // Structured chat cards (todo 23): Review opens the unified meal review
  // prefilled with the parsed items; check-in accept/keep goes through the
  // same responder the Plan screen uses; Log/Save-as-memory stay behind the
  // approval flow by routing through the normal send pipeline.
  const handleStartReview = (items: readonly LoggingFlowReviewItem[]) => {
    console.log("[Frontend][Chat] Meal estimate review started", { itemCount: items.length });
    startFlow("review", { items, source: "chat" });
  };
  const handleRespondCheckin = useMemo(
    () => createCheckinResponder(respondToWeeklyCheckin),
    [respondToWeeklyCheckin],
  );
  const handleSendCardMessage = (text: string) => {
    console.log("[Frontend][Chat] Card action sent as message", { textLength: text.length });
    handleSend(text);
  };

  const handleApprove = async (
    approvalId: string,
    approved: boolean,
    messageId: string,
    reason?: string,
  ): Promise<boolean> => {
    if (!threadId) return false;
    const existingRecovery = approvalRecoveriesRef.current[approvalId];
    const approvalItem = visibleItems.find(
      (item) => item.kind === "approval-request" && item.approvalId === approvalId,
    );
    if (!approvalItem || approvalItem.kind !== "approval-request") return false;
    if (
      !canStartApprovalDecision(
        approvalBusyRef.current,
        approvalId,
        approvalItem.approvalStatus,
      )
    ) {
      return false;
    }
    approvalBusyRef.current.add(approvalId);
    setIsSending(true);
    const recoveryBase = {
      approvalId,
      approvalGroupId: approvalItem.approvalGroupId,
      toolName: approvalItem.toolName,
      args: approvalItem.args,
      messageId,
      messageIndex: approvalItem.messageIndex,
      approved,
      ...(reason !== undefined ? { reason } : {}),
    } satisfies Omit<ApprovalRecovery, "responseMessageId" | "status">;
    const persistedResponseMessageId =
      existingRecovery?.responseMessageId ?? approvalItem.responseMessageId;
    const setRecovery = (recovery: ApprovalRecovery) => {
      const next = { ...approvalRecoveriesRef.current, [approvalId]: recovery };
      approvalRecoveriesRef.current = next;
      setApprovalRecoveries(next);
    };
    setRecovery({
      ...recoveryBase,
      ...(persistedResponseMessageId !== undefined
        ? { responseMessageId: persistedResponseMessageId }
        : {}),
      status: persistedResponseMessageId === undefined ? "recording" : "retry",
    });
    console.log("[Frontend][Chat] Approval decision started", {
      approvalId,
      approvalGroupId: approvalItem.approvalGroupId,
      approved,
      reusingResponse: persistedResponseMessageId !== undefined,
    });
    try {
      const result = persistedResponseMessageId
        ? { messageId: persistedResponseMessageId }
        : await approve({ threadId, approvalId, approved, reason });
      const recordedRecovery: ApprovalRecovery = {
        ...recoveryBase,
        responseMessageId: result.messageId,
        status: "recorded",
      };
      setRecovery(recordedRecovery);
      const plan = getApprovalContinuationPlan(
        items,
        { ...approvalRecoveriesRef.current, [approvalId]: recordedRecovery },
        { approvalId, responseMessageId: result.messageId },
      );
      if (plan.kind === "waiting") {
        console.log("[Frontend][Chat] Approval decision recorded", {
          approvalId,
          approvalGroupId: plan.approvalGroupId,
          recordedCount: plan.approvalIds.filter(
            (groupApprovalId) =>
              approvalRecoveriesRef.current[groupApprovalId]?.responseMessageId !== undefined,
          ).length,
          approvalCount: plan.approvalIds.length,
        });
        return true;
      }
      if (continuationBusyRef.current.has(plan.approvalGroupId)) return true;
      continuationBusyRef.current.add(plan.approvalGroupId);
      const continuingRecoveries = { ...approvalRecoveriesRef.current };
      plan.approvalIds.forEach((groupApprovalId) => {
        const recovery = continuingRecoveries[groupApprovalId];
        if (recovery) {
          continuingRecoveries[groupApprovalId] = {
            ...recovery,
            status: "continuing",
          };
        }
      });
      approvalRecoveriesRef.current = continuingRecoveries;
      setApprovalRecoveries(continuingRecoveries);
      console.log("[Frontend][Chat] Approval continuation scheduled", {
        approvalGroupId: plan.approvalGroupId,
        approvalCount: plan.approvalIds.length,
        promptMessageId: plan.promptMessageId,
      });
      try {
        const clientNow = Date.now();
        await sendMessage({
          threadId,
          prompt: "",
          promptMessageId: plan.promptMessageId,
          clientNow,
          ...clientLogTimes(clientNow),
        });
      } catch (err: unknown) {
        const retryRecoveries = { ...approvalRecoveriesRef.current };
        plan.approvalIds.forEach((groupApprovalId) => {
          const recovery = retryRecoveries[groupApprovalId];
          if (recovery) {
            retryRecoveries[groupApprovalId] = { ...recovery, status: "retry" };
          }
        });
        approvalRecoveriesRef.current = retryRecoveries;
        setApprovalRecoveries(retryRecoveries);
        throw err;
      } finally {
        continuationBusyRef.current.delete(plan.approvalGroupId);
      }
      console.log("[Frontend][Chat] Approval continuation queued", {
        approvalId,
        approvalGroupId: plan.approvalGroupId,
        promptMessageId: plan.promptMessageId,
      });
      return true;
    } catch (err: unknown) {
      if (isCreditError(err)) {
        setShowCreditDrawer(true);
        console.warn("[Frontend][Chat] Credit or quota issue detected", { phase: "approval-continuation" });
      }
      console.error("[Frontend][Chat] Approval continuation failed", {
        approvalId,
        approved,
        error: err instanceof Error ? err.message : String(err),
      });
      const currentRecovery = approvalRecoveriesRef.current[approvalId];
      const failedRecovery = currentRecovery ?? existingRecovery;
      if (failedRecovery) {
        const next = {
          ...approvalRecoveriesRef.current,
          [approvalId]: {
            ...failedRecovery,
            status:
              failedRecovery.responseMessageId === undefined
                ? "retry-decision"
                : "retry",
          } satisfies ApprovalRecovery,
        };
        approvalRecoveriesRef.current = next;
        setApprovalRecoveries(next);
      }
      return false;
    } finally {
      approvalBusyRef.current.delete(approvalId);
      setIsSending(false);
    }
  };

  const handleEdit = async (
    approvalId: string,
    messageId: string,
    template: string,
  ): Promise<boolean> => {
    const succeeded = await handleApprove(
      approvalId,
      false,
      messageId,
      "User chose to edit",
    );
    if (!succeeded) return false;
    setInput(template);
    setRows(Math.min(5, Math.max(1, (template.match(/\n/g) || []).length + 1)));
    requestAnimationFrame(() => composerRef.current?.focus());
    console.log("[Frontend][Chat] Approval edit template ready", { approvalId });
    return true;
  };

  const appendTranscript = (transcript: string) => {
    const textarea = composerRef.current;
    const selection = textarea
      ? { start: textarea.selectionStart, end: textarea.selectionEnd }
      : { start: input.length, end: input.length };
    const next = appendTranscriptAtSelection(input, selection, transcript);
    const scheduledRevision = textareaRevisionRef.current;
    setInput(next.value);
    setRows(Math.min(5, Math.max(1, (next.value.match(/\n/g) || []).length + 1)));
    requestAnimationFrame(() => {
      if (!shouldRestoreTextareaSelection(scheduledRevision, textareaRevisionRef.current)) {
        console.debug("[Frontend][Chat] Skipped stale textarea restoration", {
          phase: "audio-insert",
          scheduledRevision,
          currentRevision: textareaRevisionRef.current,
        });
        return;
      }
      const currentTextarea = composerRef.current;
      if (!currentTextarea) return;
      currentTextarea.focus();
      currentTextarea.setSelectionRange(next.selection.start, next.selection.end);
    });
  };

  const handleAudioUpload = async (blob: Blob) => {
    setIsTranscribing(true);
    setAudioError(null);
    try {
      const audioBase64 = await encodeBlobAsBase64(blob);
      const result = await transcribeAudio({ audioBase64, mimeType: blob.type || "audio/webm" });
      if (insertVoiceTranscript(result.text, appendTranscript)) {
        console.log("[Frontend][Chat] Audio transcription inserted into composer", {
          textLength: result.text.trim().length,
          model: result.model,
          language: result.language,
        });
      }
      setAudioError(null);
    } catch (err: unknown) {
      setAudioError("Could not transcribe audio. Please try again.");
      console.error("[Frontend][Chat] Audio transcription flow failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setIsTranscribing(false);
    }
  };

  const stopRecording = () => {
    activeRecordingRef.current?.stop();
    activeRecordingRef.current = null;
    setIsRecording(false);
  };

  const startRecording = async () => {
    if (isRecording || isTranscribing || !isVoiceRecordingSupported()) {
      setAudioError(VOICE_RECORDING_ERROR_MESSAGES.unsupported);
      console.warn("[Frontend][Chat] Audio recording unsupported", {
        hasMediaDevices: Boolean(navigator.mediaDevices?.getUserMedia),
        hasMediaRecorder: typeof MediaRecorder !== "undefined",
      });
      return;
    }
    const result = await startVoiceRecording({
      onBlob: (blob) => {
        void handleAudioUpload(blob);
      },
      onRecorderError: () => setAudioError("Recording failed. Please try again."),
    });
    if (!result.ok) {
      setAudioError(VOICE_RECORDING_ERROR_MESSAGES[result.error]);
      return;
    }
    activeRecordingRef.current = result.recording;
    setAudioError(null);
    setIsRecording(true);
    console.log("[Frontend][Chat] Audio recording started", {
      mimeType: result.recording.mimeType,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    textareaRevisionRef.current += 1;
    setInput(value);
    const newlineCount = (value.match(/\n/g) || []).length;
    setRows(Math.min(5, Math.max(1, newlineCount + 1)));
  };

  const removeAttachedImage = () => {
    if (imagePreviewUrlRef.current !== null) URL.revokeObjectURL(imagePreviewUrlRef.current);
    imagePreviewUrlRef.current = null;
    setAttachedImage(null);
    setImageUploadState({ phase: "idle" });
    setImageError(null);
    console.log("[Frontend][Chat] Attached image removed");
  };

  const startImageUpload = (file: File) => {
    const uploadingState = beginImageUpload(file);
    if (imageLimits === undefined) {
      setImageUploadState(failImageUpload(uploadingState, "Image upload is still loading. Try again."));
      return;
    }
    const validationMessage = imageFileValidationError(file, imageLimits);
    if (validationMessage !== null) {
      setImageUploadState(failImageUpload(uploadingState, validationMessage));
      setImageError(validationMessage);
      console.warn("[Frontend][Chat] Attached image rejected", {
        fileName: file.name,
        reason: validationMessage,
      });
      return;
    }
    setImageUploadState(uploadingState);
    setImageError(null);
    console.log("[Frontend][Chat] Attached image upload started", {
      fileName: file.name,
      fileSize: file.size,
    });
    void uploadImagePipeline(file, { generateUploadUrl, claimUpload })
      .then(({ storageId, previewUrl }) => {
        if (imagePreviewUrlRef.current !== null) URL.revokeObjectURL(imagePreviewUrlRef.current);
        imagePreviewUrlRef.current = previewUrl;
        setAttachedImage({ storageId, previewUrl, fileName: file.name });
        setImageUploadState({ phase: "idle" });
        console.log("[Frontend][Chat] Attached image upload claimed", { fileName: file.name });
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        setImageUploadState(failImageUpload(uploadingState, message));
        setImageError("Could not attach image. Try again.");
        console.error("[Frontend][Chat] Attached image upload failed", {
          fileName: file.name,
          error: message,
        });
      });
  };

  const handleImageInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file !== undefined) startImageUpload(file);
  };

  const handleSelectionChange = () => {
    textareaRevisionRef.current += 1;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };

  const handleSendClick = () => {
    handleSend(input);
  };

  const isEmpty = input.trim().length === 0 && attachedImage === null;
  const isDisabled =
    isEmpty || isAgentRunning || isTranscribing || isRecording || imageUploadState.phase === "uploading" || hasOpenApprovalFlow;
  const todayProgressPercent = today
    ? Math.min(100, Math.max(0, (today.consumedKcal / (today.goalKcal || 1)) * 100))
    : 0;

  const composer = (
      <div className="border-t border-border bg-surface/95 p-3 backdrop-blur" data-testid="chat-composer-slot" data-tour="chat">
        <div className="mx-auto flex max-w-xl flex-col gap-1">
          {attachedImage !== null && (
            <div className="flex items-center gap-2 rounded-xl border border-border bg-muted p-2" data-testid="chat-image-preview">
              <img src={attachedImage.previewUrl} alt="Attached preview" className="h-12 w-12 rounded-lg object-cover" />
              <span className="min-w-0 flex-1 truncate text-xs text-text-muted">{attachedImage.fileName}</span>
              <button
                type="button"
                onClick={removeAttachedImage}
                className="flex h-11 w-11 shrink-0 select-none items-center justify-center rounded-xl text-text-muted transition-all duration-200 ease-out active:scale-[0.98] active:bg-surface"
                aria-label="Remove attached image"
                data-testid="remove-image-btn"
              >
                <X size={18} />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              onChange={handleImageInputChange}
              data-testid="image-input"
            />
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              disabled={imageLimits === undefined || imageUploadState.phase === "uploading" || hasOpenApprovalFlow}
              className="flex h-11 w-11 flex-shrink-0 select-none items-center justify-center rounded-xl border border-border bg-surface text-text transition-all duration-200 ease-out active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Attach image"
              data-testid="image-attach-btn"
            >
              {imageUploadState.phase === "uploading" ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={21} />}
            </button>
            <textarea
              data-testid="message-input"
              ref={composerRef}
              value={input}
              onChange={handleInputChange}
              onSelect={handleSelectionChange}
              onKeyDown={handleKeyDown}
              rows={rows}
               placeholder="Message BiteTrack AI..."
              className={cn(
                "w-full flex-1 resize-none rounded-xl border border-border bg-muted px-4 py-3 text-base",
                "text-text placeholder:text-text-muted caret-accent",
                "focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
                "min-h-[44px] max-h-32",
              )}
            />
            <button
              type="button"
              data-testid="audio-btn"
              onClick={() => (isRecording ? stopRecording() : void startRecording())}
              disabled={isTranscribing || hasOpenApprovalFlow}
              className={cn(
                "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-border bg-surface text-text transition-all duration-200 ease-out",
                "active:scale-[0.98] select-none disabled:cursor-not-allowed disabled:opacity-50",
                isRecording && "border-danger/40 bg-danger/10 text-danger",
              )}
              aria-label={isRecording ? "Stop recording" : "Record voice message"}
            >
              {isTranscribing ? <Loader2 size={20} className="animate-spin" /> : isRecording ? <Square size={18} fill="currentColor" /> : <AudioLines size={22} />}
            </button>
            <button
              type="button"
              data-testid="send-btn"
              onClick={handleSendClick}
              disabled={isDisabled}
              className={cn(
                "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-accent text-white transition-all duration-200 ease-out",
                "active:scale-[0.98] select-none active:bg-accent-dark",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
              aria-label="Send message"
            >
              <Send size={22} />
            </button>
          </div>
        </div>
         <ChatThreadMenu
           isStartingNewChat={isStartingNewChat}
           isOpen={showThreadHistory}
           threadId={threadId}
           onToggle={() => setShowThreadHistory((visible) => !visible)}
           onNewChat={() => void handleNewChat()}
           onSelectThread={handleSelectThread}
         />
        {audioError && <p className="mt-2 px-2 text-xs text-danger" role="status" data-testid="audio-status">{audioError}</p>}
        {imageError && <p className="mt-2 px-2 text-xs text-danger" role="status" data-testid="image-status">{imageError}</p>}
       </div>
  );

  if (status === "LoadingFirstPage") {
    return (
      <>
        <div
          className="flex min-h-0 w-full flex-1 flex-col"
          data-testid="chat-container"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 py-4">
            <Skeleton className="h-16 w-3/4 self-start" />
            <Skeleton className="h-16 w-3/4 self-end" />
            <Skeleton className="h-16 w-2/3 self-start" />
          </div>
        </div>
        {composerTarget !== null ? createPortal(composer, composerTarget) : null}
      </>
    );
  }

  return (
    <>
      <div
        className="flex min-h-0 w-full flex-1 flex-col"
        data-testid="chat-container"
      >
        <div ref={messageListRef} className="flex flex-col pt-4" data-testid="message-list">
        <div className="flex flex-col gap-3">
          {today && (
            <div
              data-testid="today-bar"
               className="sticky top-[var(--app-header-h,4.5rem)] z-10 mb-3 flex items-center gap-3 rounded-xl bg-accent-light px-4 py-2 shadow-sm"
            >
              <ProgressRing
                value={todayProgressPercent}
                size={40}
                stroke={4}
              >
                <span className="text-xs font-bold">
                  {Math.round(todayProgressPercent)}%
                </span>
              </ProgressRing>
              <div className="flex-1">
                <div
                  className="text-sm font-semibold text-text"
                  data-testid="today-bar-consumed"
                >
                  {today.consumedKcal} / {today.goalKcal ?? "—"} kcal
                </div>
                <div className="text-xs text-text-muted">
          {formatKcalBalance(today.remainingKcal)}
                </div>
                <div className="text-[10px] leading-tight text-text-muted/80">
                  Exercise isn't added back to your budget.
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {visibleItems.length === 0 ? (
              <EmptyState
                icon={MessageCircle}
                title="Hi! Tell me what you ate"
                message="Or snap a food photo from Quick Log 📸"
                action={
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => onSwitchScreen?.("today")}
                  >
                    View today's summary
                  </Button>
                }
              />
            ) : (
              <>
                {visibleItems.map((item, index) => (
                  <MessageBubble
                    key={`${item.messageId}-${index}`}
                    item={item}
                    index={index}
                    onApprove={handleApprove}
                    onEdit={handleEdit}
                    onSwitchScreen={onSwitchScreen}
                    onSendMessage={handleSendCardMessage}
                    onStartReview={handleStartReview}
                    onRespondCheckin={handleRespondCheckin}
                  />
                ))}
                {isTyping && <TypingIndicator />}
              </>
            )}
            <div ref={bottomRef} className="h-0 w-full shrink-0" />
          </div>
        </div>
        </div>
      </div>
      {composerTarget !== null ? createPortal(composer, composerTarget) : null}
      {showCreditDrawer && <CreditDrawer onClose={() => setShowCreditDrawer(false)} />}
    </>
  );
}
